import React, { useEffect, useRef, useState } from 'react';
import { TextInput, UnstyledButton } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDeleteLeft, faPencil, faPlus, faXmark } from '@fortawesome/free-solid-svg-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { Button } from '../../../../../components/Button';
import { NumericKeypad } from '../../../../../components/Keypad/NumericKeypad';
import { noSelectionTextInputProps } from '../../receipt/receipt-input.utils';
import classes from './general-item-modal.module.scss';
import {
  appendPriceDigit,
  appendQuantityDigit,
  backspaceBuffer,
  computeGeneralItemTotal,
  GENERAL_ITEM_DEFAULT_PRICE,
  GENERAL_ITEM_DEFAULT_QUANTITY,
  GENERAL_ITEM_NAME_MAX,
  isGeneralItemValid,
  isPriceValid,
  isQuantityValid,
  parseGeneralItemNumber,
  type GeneralItemField,
} from './general-item-modal';

export type GeneralItemPayload = {
  unitPrice: number;
  quantity: number;
  name: string | null;
  kitchenNote?: string | null;
  printerIds: number[];
  kds: boolean;
};

export type GeneralItemPrintTarget = { id: number; name: string };

export type GeneralItemModalProps = {
  isOpen: boolean;
  isSaving?: boolean;
  isManager: boolean;
  activePrinters: GeneralItemPrintTarget[];
  isKdsAvailable: boolean;
  onClose: () => void;
  onConfirm: (payload: GeneralItemPayload) => void | Promise<void>;
};

const GeneralItemModalComponent: React.FC<GeneralItemModalProps> = ({
  isOpen,
  isSaving = false,
  isManager,
  activePrinters,
  isKdsAvailable,
  onClose,
  onConfirm,
}) => {
  const [activeField, setActiveField] = useState<GeneralItemField>('price');
  const [quantityBuffer, setQuantityBuffer] = useState(GENERAL_ITEM_DEFAULT_QUANTITY);
  const [priceBuffer, setPriceBuffer] = useState(GENERAL_ITEM_DEFAULT_PRICE);
  const [isNegativePrice, setIsNegativePrice] = useState(false);
  const [name, setName] = useState('');
  const [kitchenNote, setKitchenNote] = useState('');
  const [isEditingName, setIsEditingName] = useState(false);
  const [selectedPrinterIds, setSelectedPrinterIds] = useState<number[]>([]);
  const [isKdsSelected, setIsKdsSelected] = useState(false);
  const freshRef = useRef<{ quantity: boolean; price: boolean }>({ quantity: true, price: true });
  const submitLockRef = useRef(false);

  // Resets on the open transition only: printers/KDS arriving later must not wipe user input.
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setActiveField('price');
    setQuantityBuffer(GENERAL_ITEM_DEFAULT_QUANTITY);
    setPriceBuffer(GENERAL_ITEM_DEFAULT_PRICE);
    setIsNegativePrice(false);
    setName('');
    setKitchenNote('');
    setIsEditingName(false);
    setSelectedPrinterIds([]);
    setIsKdsSelected(false);
    freshRef.current = { quantity: true, price: true };
    submitLockRef.current = false;
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const pressDigit = (digit: string) => {
    if (isSaving) return;
    if (activeField === 'quantity') {
      const fresh = freshRef.current.quantity;
      freshRef.current.quantity = false;
      setQuantityBuffer((prev) => appendQuantityDigit(fresh ? '' : prev, digit));
    } else {
      const fresh = freshRef.current.price;
      freshRef.current.price = false;
      setPriceBuffer((prev) => appendPriceDigit(fresh ? '' : prev, digit));
    }
  };

  const toggleNegative = () => {
    if (isSaving || !isManager) return;
    const next = !isNegativePrice;
    if (next) {
      // Refund lines are always quantity 1 (backend rejects >1); reset qty and move focus to price.
      setQuantityBuffer('1');
      freshRef.current.quantity = true;
      setActiveField('price');
      setIsEditingName(false);
    } else {
      setQuantityBuffer(GENERAL_ITEM_DEFAULT_QUANTITY);
      freshRef.current.quantity = true;
    }
    setIsNegativePrice(next);
  };

  const togglePrinter = (printerId: number) => {
    if (isSaving) return;
    setSelectedPrinterIds((prev) => (
      prev.includes(printerId) ? prev.filter((id) => id !== printerId) : [...prev, printerId]
    ));
  };

  const pressBackspace = () => {
    if (isSaving) return;
    if (activeField === 'quantity') {
      freshRef.current.quantity = false;
      setQuantityBuffer((prev) => backspaceBuffer(prev));
    } else {
      freshRef.current.price = false;
      setPriceBuffer((prev) => backspaceBuffer(prev));
    }
  };

  const quantityDisplay = quantityBuffer || 'הקלד כמות';
  const priceDisplay = priceBuffer ? `${isNegativePrice ? '-' : ''}₪${priceBuffer}` : 'הקלד מחיר';
  const totalPreview = computeGeneralItemTotal(quantityBuffer, priceBuffer) * (isNegativePrice ? -1 : 1);
  const availableSelectedPrinterIds = selectedPrinterIds.filter((id) => activePrinters.some((printer) => printer.id === id));
  const hasTargets = availableSelectedPrinterIds.length > 0 || (isKdsSelected && isKdsAvailable);
  const hasNumericValues = isGeneralItemValid(quantityBuffer, priceBuffer);
  const canConfirm = !isSaving && hasNumericValues && (isNegativePrice || hasTargets)
    && !(isNegativePrice && totalPreview === 0);
  const missingFields = [
    ...(!isPriceValid(priceBuffer) ? ['מחיר'] : []),
    ...(!isQuantityValid(quantityBuffer) ? ['כמות'] : []),
    ...(!isNegativePrice && !hasTargets ? ['יעד הדפסה'] : []),
  ];
  const missingText = missingFields.length > 1
    ? `${missingFields.slice(0, -1).join(', ')} ו${missingFields[missingFields.length - 1]}`
    : missingFields[0];

  const handleConfirm = () => {
    if (!canConfirm || submitLockRef.current) return;
    if (isNegativePrice && !isManager) return;
    submitLockRef.current = true;
    const trimmedName = name.trim();
    const payload: GeneralItemPayload = {
      unitPrice: parseGeneralItemNumber(priceBuffer) * (isNegativePrice ? -1 : 1),
      quantity: parseGeneralItemNumber(quantityBuffer),
      name: isNegativePrice || trimmedName.length === 0 ? null : trimmedName,
      ...(!isNegativePrice && kitchenNote.trim() ? { kitchenNote: kitchenNote.trim() } : {}),
      printerIds: isNegativePrice ? [] : availableSelectedPrinterIds,
      kds: !isNegativePrice && isKdsSelected && isKdsAvailable,
    };
    void Promise.resolve(onConfirm(payload)).finally(() => {
      submitLockRef.current = false;
    });
  };

  return (
    <div
      className={classes.overlay}
      role="dialog"
      aria-modal="true"
      aria-label="פריט כללי"
      dir="rtl"
      onClick={(event) => {
        if (event.target === event.currentTarget && !isSaving) {
          onClose();
        }
      }}
    >
      <div className={classes.modal}>
        <header className={classes.header} data-testid="general-item-header">
          <div className={classes.titleGroup}>
            {isEditingName && !isNegativePrice ? (
              <TextInput
                value={name}
                onChange={(event) => setName(event.currentTarget.value)}
                onBlur={() => setIsEditingName(false)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === 'Escape') {
                    event.preventDefault();
                    setIsEditingName(false);
                  }
                }}
                aria-label="שם הפריט"
                placeholder="פריט כללי"
                maxLength={GENERAL_ITEM_NAME_MAX}
                disabled={isSaving}
                autoFocus
                classNames={{ input: classes.titleInput }}
                data-testid="general-item-title-input"
                {...noSelectionTextInputProps}
              />
            ) : (
              <UnstyledButton
                type="button"
                className={classes.titleButton}
                onClick={() => setIsEditingName(true)}
                disabled={isSaving || isNegativePrice}
                aria-label="עריכת שם הפריט"
                data-testid="general-item-edit-title"
              >
                <h2 className={classes.title}>{!isNegativePrice && name.trim() ? name.trim() : 'פריט כללי'}</h2>
                {!isNegativePrice && <FontAwesomeIcon icon={faPencil as IconProp} className={classes.titlePencil} />}
              </UnstyledButton>
            )}
            {!isNegativePrice && <span className={classes.targetsHint}>ניתן לעריכה</span>}
          </div>
          {isNegativePrice && (
            <p className={classes.refundBadge} data-testid="general-item-refund-badge">
              זיכוי
            </p>
          )}
          <Button
            variant="icon"
            ariaLabel="סגור"
            className={classes.close}
            onClick={onClose}
            disabled={isSaving}
            dataTestId="general-item-close"
          >
            <FontAwesomeIcon icon={faXmark as IconProp} />
          </Button>
        </header>

        <div className={classes.body}>
          <UnstyledButton
            component="button"
            type="button"
            className={`${classes.tile} ${activeField === 'price' ? classes.tileActive : ''}`}
            onClick={() => setActiveField('price')}
            disabled={isSaving}
            data-testid="general-item-price-tile"
          >
            <span className={classes.tileLabel}>מחיר <span className={classes.required} aria-hidden="true">*</span></span>
            <span
              className={`${classes.tileValue} ${!priceBuffer ? classes.tilePlaceholder : ''} ${isNegativePrice ? classes.tileValueNegative : ''}`}
              dir={priceBuffer ? 'ltr' : 'rtl'}
              data-testid="general-item-price-value"
            >
              {priceDisplay}
            </span>
          </UnstyledButton>

          {!isNegativePrice && (
            <>
              <UnstyledButton
                component="button"
                type="button"
                className={`${classes.tile} ${activeField === 'quantity' ? classes.tileActive : ''}`}
                onClick={() => setActiveField('quantity')}
                disabled={isSaving}
                data-testid="general-item-quantity-tile"
              >
                <span className={classes.tileLabel}>כמות <span className={classes.required} aria-hidden="true">*</span></span>
                <span className={`${classes.tileValue} ${!quantityBuffer ? classes.tilePlaceholder : ''}`} data-testid="general-item-quantity-value">
                  {quantityDisplay}
                </span>
              </UnstyledButton>

              <label className={classes.tile}>
                <span className={classes.tileLabel}>הערה</span>
                <TextInput
                  type="text"
                  variant="unstyled"
                  classNames={{ root: classes.noteRoot, input: classes.noteInput }}
                  dir="rtl"
                  aria-label="הערה"
                  value={kitchenNote}
                  maxLength={GENERAL_ITEM_NAME_MAX}
                  placeholder="לא חובה"
                  disabled={isSaving}
                  onChange={(event) => setKitchenNote(event.target.value)}
                  {...noSelectionTextInputProps}
                  data-testid="general-item-name-input"
                />
              </label>
            </>
          )}

          {!isNegativePrice && (
            <div className={classes.targetsField}>
              <div className={classes.targetsHeader}>
                <span className={classes.targetsLabel}>יעד הדפסה <span className={classes.required} aria-hidden="true">*</span></span>
                {!hasTargets && (
                  <span className={classes.targetsHint} data-testid="general-item-targets-hint">
                    בחר לפחות יעד אחד
                  </span>
                )}
              </div>
              <div className={classes.targetsGrid}>
                {activePrinters.map((printer) => (
                  <UnstyledButton
                    key={printer.id}
                    component="button"
                    type="button"
                    className={`${classes.targetTile} ${selectedPrinterIds.includes(printer.id) ? classes.targetTileActive : ''}`}
                    onClick={() => togglePrinter(printer.id)}
                    disabled={isSaving}
                    aria-pressed={selectedPrinterIds.includes(printer.id)}
                    data-testid={`general-item-target-printer-${printer.id}`}
                  >
                    {printer.name}
                    {!selectedPrinterIds.includes(printer.id) && <FontAwesomeIcon icon={faPlus as IconProp} />}
                  </UnstyledButton>
                ))}
                {isKdsAvailable && (
                  <UnstyledButton
                    component="button"
                    type="button"
                    className={`${classes.targetTile} ${isKdsSelected ? classes.targetTileActive : ''}`}
                    onClick={() => setIsKdsSelected((prev) => !prev)}
                    disabled={isSaving}
                    aria-pressed={isKdsSelected}
                    data-testid="general-item-target-kds"
                  >
                    KDS
                    {!isKdsSelected && <FontAwesomeIcon icon={faPlus as IconProp} />}
                  </UnstyledButton>
                )}
              </div>
            </div>
          )}

          <NumericKeypad
            keypadClassName={classes.keypad}
            keyClassName={classes.key}
            dataTestId="general-item-keypad"
            disabled={isSaving}
            onDigit={pressDigit}
            onBackspace={pressBackspace}
            backspaceAriaLabel="מחק ספרה"
            backspaceIcon={<FontAwesomeIcon icon={faDeleteLeft as IconProp} />}
            backspaceClassName={isManager ? undefined : `${classes.key} ${classes.backspaceWide}`}
            leftKey={isManager ? {
              label: '−',
              onClick: toggleNegative,
              disabled: isSaving,
              className: `${classes.key} ${classes.minusKey} ${isNegativePrice ? classes.minusKeyActive : ''}`,
              ariaLabel: 'זיכוי',
              dataTestId: 'general-item-minus-key',
            } : undefined}
          />

          <div className={classes.totalRow} data-testid="general-item-total">
            <span className={classes.totalAmount}>
              <span className={classes.totalLabel}>סה״כ</span>
              {missingFields.length > 0 && (
                <span className={classes.totalCaption} data-testid="general-item-missing-fields">
                  {missingFields.length > 1 ? 'חסרים' : 'חסר'} {missingText}
                </span>
              )}
              {missingFields.length === 0 && totalPreview === 0 && !isNegativePrice && (
                <span className={classes.totalCaption} data-testid="general-item-free-caption">
                  פריט ללא חיוב
                </span>
              )}
            </span>
            <span
              className={`${classes.totalValue} ${isNegativePrice ? classes.totalValueNegative : ''}`}
              dir="ltr"
            >
              {hasNumericValues ? `${totalPreview < 0 ? '-' : ''}₪${Math.abs(totalPreview)}` : '—'}
            </span>
          </div>

          <div className={classes.actions}>
            <Button
              variant="cancel"
              size="lg"
              className={classes.cancelButton}
              onClick={onClose}
              disabled={isSaving}
              dataTestId="general-item-cancel"
            >
              ביטול
            </Button>
            <Button
              variant="confirm"
              size="lg"
              className={classes.confirmButton}
              onClick={handleConfirm}
              disabled={!canConfirm}
              dataTestId="general-item-confirm"
            >
              {isSaving ? 'שומר...' : 'הוספה להזמנה'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const GeneralItemModal = React.memo(GeneralItemModalComponent);
